import React, { useMemo } from 'react';
import styled from 'styled-components';
import { uniq } from 'lodash';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import Selector, {
  SelectorOptions,
} from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useAllIndicatorsMin } from 'shared/apollo/pageHooks/useAllIndicators';
import { AnswerInput, useProgram } from 'shared/providers/ProgramProvider';
import { subServiceQuestionId } from 'shared/providers/IndicatorProvider/utils';
import { INDICATOR_STAGES } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { IndicatorDetail } from 'npo/pages/ReportBuilder/Components/SectionIndicators';
import {
  CapacityDevelopmentHelpText,
  CAPACITY_DEVELOPMENT_NONE_TEXT,
} from 'npo/pages/ReportBuilder/Components/SectionCheckboxes';
import SelectorField from './SelectorField';

const HelpWrapper = styled.div`
  width: 700px;
  max-width: 100%;
  margin: 0 auto;
  padding: ${SPACERS.small};
  background-color: ${COLORS.ctaPrimaryBackground};
  border-radius: 4px;
  a {
    cursor: pointer;
  }
`;

const NoneWrapper = styled.div`
  width: 700px;
  max-width: 100%;
  margin: ${SPACERS.medium} auto 0;
  padding-top: ${SPACERS.medium};
  border-top: 1px solid ${COLORS.greyBase};
`;

const parseSelected = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw.replace(/'/g, '"'));
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

interface CapacityDevelopmentFieldProps {
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
}

// Q40021 "Program Development" indicators — mirrors ReportBuilder's
// SectionCheckboxes branch for the capacity-development question: stage-6
// indicators scoped to the selected impact model (Q40003) when it has any,
// otherwise every stage-6 indicator, plus an explicit "N/A" choice that
// stores an empty list so the question still counts as answered.
const CapacityDevelopmentField: React.FC<CapacityDevelopmentFieldProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { data: indicators, loading } = useAllIndicatorsMin();
  const { answers } = useProgram();
  const selectedGroup = answers[subServiceQuestionId]?.text;

  const groups = (indicators ?? []) as IndicatorDetail[];
  const options = useMemo(() => {
    const stageId = INDICATOR_STAGES.capacityDevelopment;
    const all: string[] = [];
    const scoped: string[] = [];
    groups.forEach(group =>
      group.indicators.forEach(ind => {
        if (ind.stageId !== stageId) return;
        all.push(ind.name);
        if (group.name === selectedGroup) scoped.push(ind.name);
      }),
    );
    return uniq(scoped.length > 0 ? scoped : all);
  }, [groups, selectedGroup]);

  const definitions = useMemo(() => {
    const map: Record<string, string> = {};
    groups.forEach(g =>
      g.indicators.forEach(ind => {
        if (ind.definition && !map[ind.name]) map[ind.name] = ind.definition;
      }),
    );
    return map;
  }, [groups]);

  const selected = parseSelected(value.text);
  const noneSelected = Boolean(value.text) && selected.length === 0;

  const commit = (next: string[]) =>
    onChange({ ...value, text: JSON.stringify(next) });

  if (loading && groups.length === 0) return <TrueImpactLoading />;

  if (options.length === 0 && selected.length === 0) {
    return (
      <TrueImpactText color="critical" variant="body-small">
        No program development indicators are available yet.
      </TrueImpactText>
    );
  }

  return (
    <>
      <HelpWrapper>
        <CapacityDevelopmentHelpText />
      </HelpWrapper>
      <SelectorField>
        <SelectorOptions
          variant="checkbox"
          margin="0"
          maxWidth="100%"
          disabled={disabled}
          options={options.length === 0 ? selected : options}
          getLabel={name => name}
          getSelected={name => selected.includes(name)}
          tooltip={name => definitions[name] ?? ''}
          onClick={name => {
            if (disabled) return;
            commit(
              selected.includes(name)
                ? selected.filter(s => s !== name)
                : [...selected, name],
            );
          }}
        />
      </SelectorField>
      <NoneWrapper>
        <Selector
          variant="radio"
          label={CAPACITY_DEVELOPMENT_NONE_TEXT}
          selected={noneSelected}
          maxWidth="100%"
          disabled={disabled}
          onClick={() => !disabled && commit([])}
        />
      </NoneWrapper>
    </>
  );
};

export default CapacityDevelopmentField;
